import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  Box,
  Fab,
  Grid,
  Button,
  Dialog,
  Snackbar,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogContentText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { dataEditActions, dataActions, dataExplorerActions } from "../../store";
import { entities } from "../../config/config.dataExplorer";
import { API_URL } from "../../config/config.data";
import { StyledTextField } from "../UI/StyledTextField";
import Dropdown from "./Dropdown";

const SNACKBAR_AUTO_HIDE_DURATION = 1000;

const DatasetForm = () => {
  const dispatch = useDispatch();
  const selectedRow = useSelector((state) => state.dataExplorer.selectedRow);
  const ID = selectedRow.id;
  const selectedEntity = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );
  const entity = entities[selectedEntity];

  const [initialData, setInitialData] = useState({});
  const [formData, setFormData] = useState({});
  const [tempFieldData, setTempFieldData] = useState({});
  const [editableFields, setEditableFields] = useState({});
  const [columns, setColumns] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarAutoHideDuration, setSnackbarAutoHideDuration] =
    useState(null);
  const [confirmBack, setConfirmBack] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [errorList, setErrorList] = useState([]);
  const [editableFieldsInitialized, setEditableFieldsInitialized] =
    useState(false);

  // remove API contradictions
  const exceptions = [
    "release_status",
    "temporal_frequency",
    "update_type",
    "metadata_standard",
  ];

  // fetch initial data
  useEffect(() => {
    axios
      .get(`${API_URL}/${selectedEntity}/${ID}`)
      .then((response) => {
        const data = Object.fromEntries(
          Object.entries(response.data).filter(
            ([key]) => !exceptions.includes(key)
          )
        );
        setInitialData(data);
        setFormData(data);
        const columns = getColumns(data, entity);
        setColumns(columns);
        fetchDropdownOptions(data, columns);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [API_URL, selectedEntity, ID]);

  // helper function for empty values/existence in formData
  const isEmpty = (value, key) =>
    value == null || value === "" || !formData[key];

  // initialize editableFields (formData keys, all false)
  useEffect(() => {
    if (!editableFieldsInitialized && Object.keys(formData).length > 0) {
      const filteredKeys = Object.keys(formData).filter((key) => {
        return !isEmpty(formData[key], key) && !key.includes("id");
      });

      const initialEditableFields = filteredKeys.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});

      setEditableFields(initialEditableFields);
      setEditableFieldsInitialized(true);
    }
  }, [formData, editableFieldsInitialized]);

  // get columns and column attributes for later use
  const getColumns = (data, entity) => {
    let columns = [...(entity.columnConfig || [])];

    columns = [
      ...columns,
      ...Object.keys(data || {})
        .filter((key) => !entity.hiddenColumns?.includes(key))
        .filter(
          (key) =>
            !entity.columnConfig.map((column) => column.field).includes(key)
        )
        .map((key) => ({
          field: key,
          headerName: key,
          editable: true,
        })),
    ];

    return columns;
  };

  // format data for API request body
  const prepareData = () => {
    const data = {};
    columns.forEach((column) => {
      const entityColumn =
        column.renderEditCellProps?.entityColumnToUpdate || column.field;
      if (formData[column.field] !== undefined) {
        data[entityColumn] = formData[column.field];
      }
    });
    return data;
  };

  // fetch dropdown options, populate default dropdown value by ID
  const fetchDropdownOptions = (data, columns, useFirstOption = false) => {
    columns.forEach((column) => {
      if (column.renderEditCellProps) {
        const {
          categoriesEndpoint,
          categoriesEndpointResponseKey,
        } = column.renderEditCellProps;

        axios
          .get(`${API_URL}/${categoriesEndpoint}`)
          .then((response) => {
            const options = response.data[categoriesEndpointResponseKey];
            if (Array.isArray(options)) {
              setDropdownOptions((prev) => ({
                ...prev,
                [column.field]: options,
              }));
              // set initial dropdown value
              setFormData((prev) => ({
                ...prev,
                [column.field]: useFirstOption
                  ? options[0].id // default to first option
                  : data[column.field]?.id || data["project_id"] || null,
                  // hardcoded item query due to low case variety
              }));
            } else {
              console.error(
                `Invalid response structure for ${categoriesEndpoint}`
              );
            }
          })
          .catch((error) => {
            console.error(
              `Error fetching ${categoriesEndpoint} options:`,
              error
            );
          });
      }
    });
  };

  // update form data on change
  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  // send put request, then update state with new data
  const acceptDataSubmission = () => {
    return new Promise((resolve, reject) => {
      setSnackbarAutoHideDuration(null);
      setSnackbarOpen(true);
      setSnackbarMessage("Updating...");

      const preparedData = prepareData();

      axios
        .put(`${API_URL}/${selectedEntity}/${ID}`, preparedData)
        .then((response) => {
          axios.get(`${API_URL}/${entity.endpoint}`).then((response) => {
            const key = entity.responseBodyKey || entity.endpoint;
            dispatch(
              dataActions.setData({
                data: response.data[key],
                dataName: selectedEntity,
              })
            );
            if (selectedEntity === "sites") {
              dispatch(
                dataActions.setFilteredSiteData(response.data[key])
              );
            }
            dispatch(
              dataExplorerActions.setSelectedRow({
                ...selectedRow,
                row: { ...selectedRow.row, ...formData },
              })
            );
            setSnackbarMessage("Updated!");
            setSnackbarAutoHideDuration(SNACKBAR_AUTO_HIDE_DURATION);
            resolve();
            returnToDataset(true);
          });
        })
        .catch((error) => {
          console.log(error);
          setSnackbarMessage("Update failed!");
          setSnackbarAutoHideDuration(SNACKBAR_AUTO_HIDE_DURATION);
          reject(error);
        });
    });
  };

  // render snackbar on data submission rejection
  const rejectDataSubmission = () => {
    setSnackbarMessage("Submission error, please fix changes!");
    setSnackbarAutoHideDuration(SNACKBAR_AUTO_HIDE_DURATION * 2);
    setSnackbarOpen(true);
  };

  // check if data submission meets requirements
  const isDataValid = (formData, selectedEntity) => {
    let isValid = true;
    let errors = [];

    // access selected entity configuration
    const selectedConfig = entities[selectedEntity];

    if (!selectedConfig) {
      console.error(`Entity configuration for ${selectedEntity} not found.`);
      return false;
    }

    // access requiredCreateFields array from selectedConfig object
    const requiredFields = selectedConfig.requiredCreateFields;
    // loop through requiredFields and check value in formData
    requiredFields?.forEach((field) => {
      if (!formData[field]) {
        isValid = false;
        // find the corresponding column to get the headerName or field
        const column = selectedConfig.columnConfig.find(
          (col) => col.field === field
        );
        errors.push(column.field);
      }
    });

    setErrorList(errors);

    if (!isValid) {
      rejectDataSubmission();
    }

    return isValid;
  };

  // handle data submission
  const handleSubmit = async () => {
    if (isDataValid(formData, selectedEntity)) {
      try {
        await acceptDataSubmission();
        returnToDataset(formData !== initialData);
      } catch (error) {
        console.error("Error submitting validated data:", error);
      }
    } else {
      rejectDataSubmission();
    }
  };

  // functionality for back button confirmation
  const handleBack = () => {
    setConfirmBack(true);
  };

  // handle events if back button confirmed
  const handleConfirmBack = () => {
    setConfirmBack(false);
    returnToDataset(false);
  };

  // return to form if back button not confirmed or clicked away
  const returnToForm = () => {
    setConfirmBack(false);
  };

  // make field editable and create image of form data
  const makeFieldEditable = (field) => {
    setEditableFields((prev) => ({ ...prev, [field]: true }));
    setTempFieldData(formData[field]);
  };

  // helper function to make a field uneditable
  const makeFieldUneditable = (field) => {
    setEditableFields((prev) => ({ ...prev, [field]: false }));
  };

  // handle submission of a specific field
  const handleFieldSubmit = (field) => {
    makeFieldUneditable(field);
  };

  // handle discarding changes to a specific field
  const handleDiscard = (field) => {
    setFormData({ ...formData, [field]: tempFieldData });
    makeFieldUneditable(field);
  };

  // handle field deletion
  const handleDeleteField = (field) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      delete newFormData[field];
      return newFormData;
    });
    setEditableFields((prevEditableFields) => {
      const newEditableFields = { ...prevEditableFields };
      delete newEditableFields[field];
      return newEditableFields;
    });
  };

  // add field, populate dropdown if field is categorical
  const handleAddField = (field) => {
    const columnConfig = columns.find((col) => col.field === field);

    if (columnConfig && columnConfig.renderEditCellProps) {
      fetchDropdownOptions({}, [columnConfig], true);
    } else {
      setFormData((prev) => ({ ...prev, [field]: "" }));
    }
    makeFieldEditable(field);
  };

  // hide dataset form, show alert
  const returnToDataset = (dataChanged) => {
    dispatch(dataEditActions.showDatasetForm(false));
    dispatch(dataEditActions.showAlert(true));
    dispatch(dataEditActions.noteDataChange(dataChanged));
  };

  // jsx component + logic for field functionality buttons
  const renderButtons = (key) => {
    return (
      <Grid
        item
        xs={1}
        sx={{
          "&.MuiGrid-item": {
            paddingTop: 0,
          },
        }}
      >
        <Box sx={{ display: "flex" }}>
          {!editableFields[key] ? (
            <Fab
              sx={{ width: 20, height: 20, minHeight: 20 }}
              color="secondary"
              aria-label="edit"
              onClick={() => makeFieldEditable(key)}
            >
              <EditIcon sx={{ fontSize: 12 }} />
            </Fab>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Grid container direction="column" spacing={1}>
                <Grid item id="here we go">
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Fab
                      sx={{ width: 20, height: 20, minHeight: 20 }}
                      color="primary"
                      aria-label="save"
                      onClick={() => handleFieldSubmit(key)}
                    >
                      <CheckIcon sx={{ fontSize: 12 }} />
                    </Fab>
                    <Fab
                      sx={{ width: 20, height: 20, minHeight: 20 }}
                      color="default"
                      aria-label="cancel"
                      onClick={() => handleDiscard(key)}
                    >
                      <CancelIcon sx={{ fontSize: 12 }} />
                    </Fab>
                  </Box>
                </Grid>
                <Grid item>
                  <Box>
                    <Fab
                      sx={{ width: 20, height: 20, minHeight: 20 }}
                      color="error"
                      aria-label="delete"
                      onClick={() => handleDeleteField(key)}
                    >
                      <DeleteIcon sx={{ fontSize: 12 }} />
                    </Fab>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  // jsx component + logic for back button and confirmation dialog
  const renderBackButton = () => {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ px: 0, py: 2, color: "primary.contrastText" }}
          >
            Back
          </Button>
        </Box>

        <Dialog open={confirmBack} onClose={returnToForm}>
          <DialogTitle style={{ textAlign: "center" }}>
            Confirm Navigation
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Leave without submitting changes?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ justifyContent: "center" }}>
            <Button onClick={returnToForm} color="secondary" variant="outlined">
              No
            </Button>
            <Button
              onClick={handleConfirmBack}
              color="secondary"
              variant="contained"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // jsx component for title
  const renderTitle = () => {
    return (
      <Box>
        <Typography
          variant="h4"
          sx={{ color: "primary.contrastText", textAlign: "center", pb: 3 }}
        >
          Edit Your Dataset
        </Typography>
      </Box>
    );
  };

  // jsx component + logic for fields
  const renderFields = () => {
    return Object.keys(initialData).map((key) => {
      const columnConfig = columns.find((col) => col.field === key);
      const headerName = columnConfig ? columnConfig.headerName : key;

      // skip fields that contain 'id'
      if (key.includes("id")) {
        return null;
      }

      // check if field in editableFields
      if (!editableFields.hasOwnProperty(key)) {
        return (
          <Box key={key} sx={{ margin: "8px 0" }}>
            <Button
              variant="contained"
              onClick={() => handleAddField(key)}
              startIcon={<AddIcon />}
            >
              {headerName}
            </Button>
          </Box>
        );
      }

      const isError = errorList.includes(key);

      // check if field is categorical
      if (dropdownOptions[key]) {
        return (
          <Box key={key}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={11}>
                <Dropdown
                  column={columnConfig}
                  options={dropdownOptions[key]}
                  value={formData[key]} // Pass the ID as value
                  onChange={handleInputChange(key)}
                  disabled={!editableFields[key]}
                  fullWidth
                  error={isError}
                  margin="normal"
                />
              </Grid>
              {renderButtons(key)}
            </Grid>
          </Box>
        );
      }

      // default is text field
      return (
        <Box key={key}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={11}>
              <StyledTextField
                column={columnConfig}
                value={formData[key] || ""}
                onChange={handleInputChange(key)}
                disabled={!editableFields[key]}
                fullWidth
                error={isError}
                margin="normal"
              />
            </Grid>
            {renderButtons(key)}
          </Grid>
        </Box>
      );
    });
  };

  // jsx component for submit button
  const renderSubmitButton = () => {
    return (
      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            backgroundColor: "#3D9641",
            color: "secondary.contrastText",
            minWidth: "200px",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#45A349",
            },
            my: 0.5,
          }}
        >
          Submit Changes
        </Button>
      </Box>
    );
  };

  //jsx component for snackbar
  const renderSnackbar = () => {
    return (
      <Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarAutoHideDuration}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          action={
            snackbarAutoHideDuration ? null : (
              <CircularProgress
                size={20}
                sx={{ color: "secondary.contrastText" }}
              />
            )
          }
        />
      </Box>
    );
  };

  return (
    <Box
      id="dataset-form-view"
      sx={{
        display: "block",
        backgroundColor: "primary.light",
        px: 2,
      }}
    >
      {renderBackButton()}
      {renderTitle()}
      {renderFields()}
      {renderSubmitButton()}
      {renderSnackbar()}
    </Box>
  );
};

export default DatasetForm;
